/**
 * Open-artifact state for the agent chat canvas.
 *
 * Distinct from ``linkedContextPreviewAtom`` (Evidence side) even
 * though both render through the same ``PanelShell`` chrome. The
 * lifecycles differ:
 *
 *   - linkedContextPreviewAtom: set by the user via the chat-input
 *     link badge, cleared when the underlying link is dropped.
 *   - openArtifactAtom: set by the user clicking an "Open" affordance
 *     in the chat next to a tool-result block, cleared on close /
 *     chat change.
 *
 * One slot per scope (`artifactScope.ts`): Home, each chat, a routine pane and
 * the in-session panel each hold their own open artifact, and the active scope
 * is what the canvas shows. Cross-links replace the current artifact and close
 * clears it.
 */

import type { TabType } from '@heidi/session/contracts';
import { atom, type Getter, type PrimitiveAtom, type Setter } from 'jotai';
import { atomFamily } from 'jotai/utils';
import type {
  ConnectedCalendarEvent,
  ConnectedFileItem,
  ConnectedMailMessage,
  EligibilityPatientRow,
} from '../../../contracts';
import type { LinkedContextItem } from '../../../contracts/projects/linked-context';
import type { ObjectCreationKind } from '../../../contracts/projects/object-creation.types';

import { artifactCanvasKey } from './artifactCanvasKey';
import { activeArtifactScopeAtom, type ArtifactScopeKey } from './artifactScope';

/**
 * Composer-linked context items for the Work home + project chat.
 *
 * Single-slot by design: at most one linked context (one session OR
 * one patient) is attached to the next prompt. Linking another item
 * replaces the previous selection. Two reasons for the constraint:
 *
 *   - Cognitively, "this prompt is about X" reads cleaner than a
 *     pile of mixed contexts the agent has to disambiguate.
 *   - The preamble we serialise into the first turn stays short and
 *     unambiguous — agents do better with one explicit subject than
 *     with several "and also consider…" lines.
 *
 * Shape stays a list so the chip-render + serialise paths don't
 * need a sentinel for the empty case — they already handle empty
 * arrays cleanly.
 *
 * Scope: cleared after a successful submit so the chip doesn't
 * persist into the next prompt.
 */
export const composerLinkedItemsAtom = atom<LinkedContextItem[]>([]);

/**
 * Set the composer's linked context. Always replaces whatever was
 * previously linked (single-slot model). Pass ``null`` to clear.
 */
export const setComposerLinkedItemAtom = atom(
  null,
  (_get, set, next: LinkedContextItem | null) => {
    set(composerLinkedItemsAtom, next ? [next] : []);
  }
);

/** Reset the composer linked items (called by composer on submit). */
export const clearComposerLinkedItemsAtom = atom(null, (_get, set) => {
  set(composerLinkedItemsAtom, []);
});

/**
 * What the chat canvas currently has open in the right panel.
 *
 * Discriminated on ``type``. Each variant carries just enough id to
 * let the corresponding panel hydrate its content via the existing
 * per-domain hooks (``useQuerySessionById``, ``useQueryPatientProfiles``,
 * ``useQueryClinicalTasksBySessionId``, etc.) — we don't snapshot data
 * into the atom, so updates from the rest of the app flow through
 * naturally.
 */
export type Artifact =
  /**
   * The full session view. Optionally lands on a specific tab via
   * ``focusTab`` (and ``focusDocumentId`` when that tab is a document) —
   * otherwise it inherits the session page's default tab selection.
   *
   *   - ``create_session`` opens it on ``CONTEXT`` (the intake notes it
   *     just set) rather than the page default of the Note tab, whose
   *     empty optimistic state misleadingly implies a note is incoming.
   *   - ``generate_note_from_template`` opens it on ``DOCUMENT`` focused
   *     on the generated note, keeping its session context (note tab,
   *     transcript, other documents) around it instead of swapping the
   *     canvas to the isolated single-doc viewer (``document``).
   */
  | {
      type: 'session';
      sessionId: string;
      focusTab?: TabType;
      focusDocumentId?: string;
    }
  | { type: 'patient'; patientId: string }
  /** Same session backing as ``session`` but opens pinned to the Note tab. */
  | { type: 'note'; sessionId: string }
  /** Same session backing as ``session`` but renders the tasks list directly. */
  | { type: 'tasks'; sessionId: string }
  /** Same session backing as ``session`` but renders the clinical codes directly. */
  | { type: 'codes'; sessionId: string }
  /** Persisted letter identity, hydrated through the session's letters list query. */
  | { type: 'letter'; sessionId: string; letterId: string }
  | { type: 'document'; sessionId: string; documentId: string }
  /**
   * The template editor (``TemplateDetails``) opened in edit mode for an
   * existing template, hosted in the chat canvas instead of the
   * ``/my-templates`` route or the session-page modal.
   *
   * Carries the ``templateId`` only. The lazy panel owns resolution: on mount
   * it runs the same ``handleEditTemplate`` the route / modal editors use,
   * which resolves (GET-or-creates) the template-creation-record and seeds the
   * shared editor atoms. This converges the manual ("Edit a template" picker)
   * and agent (``open_template_editor`` tool) open paths on one resolver — the
   * opener just sets this artifact. The heavy editor is lazy-loaded behind the
   * panel so it stays out of the project-chat page bundle.
   */
  | { type: 'template-builder'; templateId: string }
  /**
   * The PDF form field editor for an existing form template. Carries
   * ``templateId`` only — the panel loads the base PDF + saved fields
   * and hosts ``FormFieldEditor`` (not the document template builder).
   */
  | { type: 'form-template'; templateId: string }
  /**
   * A library skill file or work output (user document) linked from the
   * composer. Carries ``documentId`` only — the panel hydrates via
   * ``useDocumentDownloadUrl``.
   */
  | { type: 'library-document'; documentId: string }
  /**
   * The Slide Builder editor for a generated presentation, opened from a
   * pptx `ArtefactBlock` card ("Preview presentation"). Carries the
   * `presentationId` only — the panel hydrates slides via
   * `usePresentation` (the same hook the Evidence chat's
   * `PresentationPanel` uses), so both surfaces share one fetch +
   * atom-population path.
   */
  | { type: 'presentation'; presentationId: string }
  /**
   * An agent memory saved via ``save_memory``. Carries ``memoryId`` only —
   * the panel hydrates via ``useQueryAgentMemories``.
   */
  | { type: 'memory'; memoryId: string }
  /**
   * A routine opened as the chat's right-hand context — agent create/update
   * (`create_workflow` / `update_workflow`) or the run header's "Edit routine"
   * pencil. Renders the same full overview as the Routines page
   * (``RoutineOverviewPane``). ``openInEdit`` is an explicit-action hint;
   * silent tool updates never set it.
   */
  | { type: 'workflow'; workflowId: string; openInEdit?: boolean }
  /**
   * A downloadable export opened in the artifact canvas — filled PDF forms,
   * generated PDF/Word/Excel/CSV/PPTX downloads, etc. Carries a presigned URL
   * as identity (not persisted across refresh).
   */
  | {
      type: 'document-export';
      url: string;
      filename: string;
      title: string;
      /** Force a `.pdf` extension for preview routing (filled forms). */
      forcePdfExtension?: boolean;
    }
  /**
   * A user-uploaded attachment on the run transcript. Carries durable ids only.
   * The chip and the pane both load bytes from the content route.
   */
  | {
      type: 'run-attachment';
      agentRunId: string;
      attachmentId: string;
      filename: string;
      forcePdfExtension?: boolean;
    }
  /** The "Created in this chat" list; rows are derived from the transcript in memory. */
  | { type: 'created-objects'; projectId: string }
  /**
   * A hydrated list of sessions (e.g. tomorrow / a date range). Carries
   * opaque session ids only — the panel loads names/status. Each id is a
   * chip; the selected session body renders underneath.
   */
  | {
      type: 'session-list';
      sessionIds: string[];
      /** Inclusive calendar start (YYYY-MM-DD), for the panel title. */
      startDate?: string;
      /** Inclusive calendar end (YYYY-MM-DD). */
      endDate?: string;
    }
  /**
   * Connected-app calendar events (Nango / Merge). Snapshot from the tool
   * result — external ids, no Heidi re-fetch.
   */
  | {
      type: 'calendar';
      events: ConnectedCalendarEvent[];
      provider?: string;
      start?: string;
      end?: string;
      calendarIds?: string[];
      warning?: string;
    }
  /**
   * Connected-app mail search hits (Nango / Merge). Snapshot from the tool
   * result — external ids, no Heidi re-fetch.
   */
  | {
      type: 'mail';
      messages: ConnectedMailMessage[];
      provider?: string;
      query?: string;
    }
  /**
   * Connected-app file search hits (Merge). Snapshot from the tool result.
   */
  | {
      type: 'files';
      files: ConnectedFileItem[];
      provider?: string;
      query?: string;
    }
  /**
   * Insurance / clearinghouse work product (Availity first). One artifact
   * family with variants — eligibility batch today; prior-auth + claims later.
   * Snapshot from directory tool result metadata (`integrations.apps.*`).
   */
  | {
      type: 'insurance';
      variant: 'eligibility';
      patients: EligibilityPatientRow[];
      provider?: string;
      query?: string;
      warning?: string;
    }
  /**
   * The note-push mapping verification surface for a `get_note_push_hints` read.
   * Carries the two keys only — `templateId` + `appName` — and the panel resolves
   * the discovered/stored section→field mapping STORE-FIRST from the note-push
   * candidate store (`candidateMappingsAtom`), which the timeline ingest already
   * populated. The clinician can override per-section targets in place.
   *
   * It only INFORMS the clinician's answer to a pending `ask_user` mapping
   * confirmation — it is a display + override surface and never auto-resolves the
   * turn (the clinician confirms in the `ask_user` card itself).
   */
  | { type: 'note-push-verification'; templateId: string; appName: string }
  /** Work-home Artifacts browser, opened directly from the page-level toggle. */
  | { type: 'library' };

/** One row in the "Created in this chat" panel. */
export interface CreatedObjectRef {
  /** Row dedupe key — a document create and its session pinned to that document share one. */
  id: string;
  /** Object name / title shown as the row's primary line. */
  title: string;
  /** Secondary line, e.g. "DOB 12/04/1959" for a patient. */
  subtitle?: string;
  /** ISO creation timestamp, when the tool result carried one. */
  createdAt?: string;
  /** Row icon source — a document create targets a session, so `target.type` can't pick it. */
  kind?: ObjectCreationKind;
  /** Avatar initials (patients). */
  initials?: string;
  /** Existing artifact to open when the row is clicked, if resolvable. */
  target?: Artifact;
}

/**
 * Redirects where an artifact "open" lands, for embedders that already have
 * their own surface for it (e.g. the in-session Work panel focusing the
 * session's own note/document tab instead of popping a Work-home canvas).
 * ``ProjectChat`` falls back to the {@link setOpenArtifactAtom} /
 * {@link setOpenArtifactSilentAtom} canvas atoms below when omitted.
 */
export interface ProjectArtifactSink {
  /** Direct user intent (transcript "Open", etc.) — may explicitly close
   *  the canvas (`null`), matching `setOpenArtifactAtom`. */
  onOpenArtifact: (artifact: Artifact | null) => void;
  /** Agent auto-open — always a concrete artifact; must never steal
   *  focus/reveal a sheet, matching `setOpenArtifactSilentAtom`. */
  onOpenArtifactSilent: (artifact: Artifact) => void;
}

// `undefined` means not loaded in this tab yet; `null` means closed. Entries are kept
// for the tab on purpose: a chat's slot is its in-memory preview, so coming back to
// it restores without a storage read. One small entry per scope visited.
const artifactSlotAtom = atomFamily<
  ArtifactScopeKey,
  PrimitiveAtom<Artifact | null | undefined>
>(() => atom<Artifact | null | undefined>(undefined));

// What a peek covered, per scope, so a user's ✕ on the peek goes back to it once.
const peekReturnAtom = atomFamily<ArtifactScopeKey, PrimitiveAtom<Artifact | null>>(() =>
  atom<Artifact | null>(null)
);

// XXX: any open or close that isn't a peek ends the peek, so its return can't outlive it.
const clearPeekReturn = (get: Getter, set: Setter) => {
  set(peekReturnAtom(get(activeArtifactScopeAtom)), null);
};

/**
 * The active scope's open artifact for the agent chat canvas. ``null`` means
 * the canvas is collapsed (chat full-width). Writes go to the active scope.
 */
export const openArtifactAtom = atom(
  (get): Artifact | null => get(artifactSlotAtom(get(activeArtifactScopeAtom))) ?? null,
  (get, set, next: Artifact | null) => {
    set(artifactSlotAtom(get(activeArtifactScopeAtom)), next);
  }
);

/**
 * One scope's slot, read-only, for code that acts on what it read: it can't see
 * another scope's value mid-switch. `undefined` means not loaded. Write through
 * `openArtifactInScopeAtom`.
 */
export const scopedArtifactAtom = atomFamily((key: ArtifactScopeKey) =>
  atom((get) => get(artifactSlotAtom(key)))
);

const userArtifactRevisionAtom = atom(0);

/**
 * Revision of direct user opens, replacements and closes. The agent's silent
 * auto-open does not bump it, so a pending auto-open can tell whether the user
 * touched the canvas while the tool call was resolving.
 */
export const artifactUserRevisionAtom = atom((get) => get(userArtifactRevisionAtom));

const bumpUserArtifactRevision = (get: Getter, set: Setter) => {
  set(userArtifactRevisionAtom, get(userArtifactRevisionAtom) + 1);
};

const _revealArtifact = (
  get: Getter,
  set: Setter,
  next: Artifact,
  current: Artifact | null
) => {
  bumpUserArtifactRevision(get, set);
  set(openArtifactAtom, next);
  const isSameCanvas =
    current !== null && artifactCanvasKey(current) === artifactCanvasKey(next);
  if (!isSameCanvas) {
    set(artifactOpenNonceAtom, get(artifactOpenNonceAtom) + 1);
  }
  set(artifactSheetOpenAtom, true);
};

/**
 * Mobile-only flag: is the canvas bottom-sheet currently revealed?
 *
 * On desktop the canvas split keys off ``openArtifactAtom`` alone, so
 * this atom is ignored there. On phones the canvas renders as a bottom
 * ``Sheet`` (see ``ProjectsArtifactCanvasHost``) whose visibility is
 * gated on this flag in addition to an artifact being set — which lets
 * us decouple "an artifact exists" from "the sheet is shown".
 *
 * Why decouple: the agent producing an artifact mid-conversation must
 * NOT yank a sheet over the transcript the clinician is reading. So the
 * agent auto-open path (``setOpenArtifactSilentAtom``) populates the
 * artifact without flipping this, while every direct user action
 * (transcript "Open", Work-home tile, @-mention, create hand-off) goes
 * through ``setOpenArtifactAtom`` which DOES flip it.
 *
 * Each scope has its own flag, like its artifact slot, so a sheet raised in
 * one chat doesn't make the next chat's silent opens cover its transcript.
 */
const sheetOpenSlotAtom = atomFamily<ArtifactScopeKey, PrimitiveAtom<boolean>>(() =>
  atom(false)
);

export const artifactSheetOpenAtom = atom(
  (get) => get(sheetOpenSlotAtom(get(activeArtifactScopeAtom))),
  (get, set, open: boolean) => {
    set(sheetOpenSlotAtom(get(activeArtifactScopeAtom)), open);
  }
);

/**
 * Monotonic counter bumped on every (non-null) open. Lets the canvas
 * distinguish "the agent re-pushed the SAME artifact because it just
 * edited what's already showing" (key unchanged, nonce changed → play
 * an in-place "updated" flash) from a fresh open / switch (key
 * changed → play the slide-in). Without it a same-id re-open would be
 * a no-op write and the canvas couldn't react to an in-place edit.
 */
export const artifactOpenNonceAtom = atom(0);

/**
 * Write-only setter — open / replace the current artifact, or pass
 * ``null`` to close. Splitting set / close into two atoms keeps the
 * write surfaces narrow at call sites.
 *
 * This is the "reveal" setter: every DIRECT user action that opens an
 * artifact (transcript "Open", Work-home tile, @-mention picker, the
 * create hand-off hydration) goes through here, so on mobile it also
 * flips ``artifactSheetOpenAtom`` to show the bottom sheet. The agent's
 * auto-open path must NOT reveal — it uses ``setOpenArtifactSilentAtom``.
 *
 * Re-opening the same canvas identity (e.g. clicking a tool row while
 * that session is already showing) does not bump ``artifactOpenNonceAtom``
 * so the in-place "Updated" flash is reserved for agent edits via the
 * silent setter.
 *
 * XXX: this and the silent setter write the surface on screen NOW. An open meant
 * for a surface you are about to navigate to must name it with
 * ``openArtifactInScopeAtom``, or wait for the navigation to land; otherwise it
 * stays on the surface being left.
 */
export const setOpenArtifactAtom = atom(null, (get, set, next: Artifact | null) => {
  const current = get(openArtifactAtom);
  clearPeekReturn(get, set);
  if (next === null) {
    bumpUserArtifactRevision(get, set);
    set(openArtifactAtom, null);
    set(artifactSheetOpenAtom, false);
    return;
  }
  _revealArtifact(get, set, next, current);
});

// A user open written into a scope before its host is active (a routine landing
// before navigating). The host reveals it when it becomes active, so the mobile
// sheet opens as it would for an open in place.
const pendingRevealScopeAtom = atom<ArtifactScopeKey | null>(null);

/**
 * Open, or close with ``null``, in a named scope. The active scope reveals it as
 * a user open; any other scope is written quietly and revealed when its host
 * becomes active (`revealPendingArtifactAtom`).
 */
export const openArtifactInScopeAtom = atom(
  null,
  (
    get,
    set,
    {
      scope,
      artifact,
      quiet = false,
    }: { scope: ArtifactScopeKey; artifact: Artifact | null; quiet?: boolean }
  ) => {
    // XXX: `quiet` is for loading state (a restore), which isn't a user open and reveals nothing.
    if (quiet) {
      set(artifactSlotAtom(scope), artifact);
      return;
    }
    if (scope === get(activeArtifactScopeAtom)) {
      set(setOpenArtifactAtom, artifact);
      return;
    }
    set(artifactSlotAtom(scope), artifact);
    if (artifact) set(pendingRevealScopeAtom, scope);
    else if (get(pendingRevealScopeAtom) === scope) set(pendingRevealScopeAtom, null);
  }
);

/** A host that just became active reveals an open written into its scope beforehand. */
export const revealPendingArtifactAtom = atom(
  null,
  (get, set, scope: ArtifactScopeKey) => {
    if (get(pendingRevealScopeAtom) !== scope) return;
    set(pendingRevealScopeAtom, null);
    if (!get(artifactSlotAtom(scope))) return;
    bumpUserArtifactRevision(get, set);
    set(artifactOpenNonceAtom, get(artifactOpenNonceAtom) + 1);
    set(sheetOpenSlotAtom(scope), true);
  }
);

// The artifact an inline reference opened in each scope, by identity: any later open replaces it.
const peekedArtifactAtom = atomFamily<ArtifactScopeKey, PrimitiveAtom<Artifact | null>>(
  () => atom<Artifact | null>(null)
);

/**
 * The open artifact the page should treat as its context. An inline reference
 * only peeks: the canvas shows it, but Work home stays as it was.
 */
export const focusedArtifactAtom = atom((get) => {
  const open = get(openArtifactAtom);
  return open !== null && open === get(peekedArtifactAtom(get(activeArtifactScopeAtom)))
    ? null
    : open;
});

/** Reveal an artifact from an inline reference (a chip or a composer token). */
export const peekArtifactAtom = atom(null, (get, set, next: Artifact) => {
  const scope = get(activeArtifactScopeAtom);
  const current = get(openArtifactAtom);
  // XXX: peeking from a peek keeps the original underneath; peeking over nothing remembers nothing.
  const underneath =
    current === null
      ? null
      : current === get(peekedArtifactAtom(scope))
        ? get(peekReturnAtom(scope))
        : current;
  // XXX: peeking the canvas already open has nothing underneath; returning to it would make ✕ look dead.
  const covered =
    underneath !== null && artifactCanvasKey(underneath) !== artifactCanvasKey(next)
      ? underneath
      : null;
  _revealArtifact(get, set, next, current);
  set(peekedArtifactAtom(scope), next);
  set(peekReturnAtom(scope), covered);
});

/**
 * Write-only setter — populate the open artifact WITHOUT revealing the
 * mobile sheet. Used only by the agent auto-open path so that an
 * artifact the agent produced mid-conversation is ready in the canvas
 * (and shown immediately on desktop, where visibility keys off
 * ``openArtifactAtom`` alone) but does not pop a sheet over the
 * transcript on phones. The clinician reveals it via the in-transcript
 * "Open" affordance. Still bumps the nonce so an in-place update flashes
 * when the sheet already happens to be open.
 */
export const setOpenArtifactSilentAtom = atom(null, (get, set, next: Artifact) => {
  clearPeekReturn(get, set);
  set(openArtifactAtom, next);
  set(artifactOpenNonceAtom, get(artifactOpenNonceAtom) + 1);
});

/**
 * Write-only setter — close whatever's open. Equivalent to
 * ``setOpenArtifactAtom(null)`` but reads better at call sites that
 * only ever close (the panel's X button, chat-change effect). Also
 * lowers ``artifactSheetOpenAtom`` so the mobile sheet and the panel's
 * own close button converge on a single close semantic.
 */
export const closeArtifactAtom = atom(null, (get, set) => {
  clearPeekReturn(get, set);
  bumpUserArtifactRevision(get, set);
  set(openArtifactAtom, null);
  set(artifactSheetOpenAtom, false);
});

/** A user's ✕: back to what a peek covered, once; otherwise close. */
export const dismissArtifactAtom = atom(null, (get, set) => {
  const scope = get(activeArtifactScopeAtom);
  const current = get(openArtifactAtom);
  const back =
    current !== null && current === get(peekedArtifactAtom(scope))
      ? get(peekReturnAtom(scope))
      : null;
  if (back) {
    set(peekReturnAtom(scope), null);
    _revealArtifact(get, set, back, current);
    return;
  }
  set(closeArtifactAtom);
});

/**
 * Write-only setter — close the canvas ONLY when it currently holds a routine
 * configuration.
 *
 * The routine header's card toggle. Conditional (rather than a blanket close) so
 * a session / patient artifact in the same chat survives. For a chat that isn't
 * active yet, use ``closeWorkflowArtifactInScopeAtom``.
 */
export const closeWorkflowArtifactAtom = atom(null, (get, set) => {
  if (get(openArtifactAtom)?.type !== 'workflow') return;
  clearPeekReturn(get, set);
  bumpUserArtifactRevision(get, set);
  set(openArtifactAtom, null);
  set(artifactSheetOpenAtom, false);
});

/** Close a routine configuration in a named scope, e.g. the chat a navigation is about to land on. */
export const closeWorkflowArtifactInScopeAtom = atom(
  null,
  (get, set, scope: ArtifactScopeKey) => {
    if (get(artifactSlotAtom(scope))?.type !== 'workflow') return;
    set(openArtifactInScopeAtom, { scope, artifact: null });
  }
);

/**
 * The specific fields the agent just changed on the open artifact, for
 * a per-field highlight in the panel (rather than flashing the whole
 * artifact). ``fields`` are the backend field keys (e.g. ``first_name``,
 * ``additional_context``) which map 1:1 onto the patient form's field
 * keys. ``token`` is the originating tool_call id — unique per update —
 * so the panel can replay the highlight only when a *new* update lands.
 * ``null`` once cleared (artifact switch / nothing recent).
 */
export const artifactFieldUpdateAtom = atom<{
  token: string;
  fields: ReadonlySet<string>;
} | null>(null);
